import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Dropdown, Checkbox } from "semantic-ui-react";
import Switch from "react-switch";
import {
  toggleOutbound,
  setWorkspaces,
  setParsedFields,
  setSelectedValues,
  setRequiredSetting,
  fetchOutboundWorkspaces,
} from "../../store/outboundVoiceSlice";

/**
 * OutboundVoiceWebhook
 *
 * UI for configuring outbound voice webhook rules.
 *
 * INPUT PROPS:
 * - showOutbound (fallbackWebhook):
 *   If fallback webhook is enabled, outbound configuration is hidden.
 *
 * STATE (Redux):
 * - outboundVoice slice manages:
 *   - enabled toggle
 *   - available workspaces
 *   - selected workspaces
 *   - selected filter fields & values
 *   - required setting per workspace
 */
const OutboundVoiceWebhook = ({ showOutbound: fallbackWebhook }) => {
  const dispatch = useDispatch();

  // Read outbound voice state from Redux
  const outbound = useSelector((s) => s.outboundVoice);
  console.log("Outbound Voice State:", outbound);

  /**
   * Show outbound configuration only when:
   * - Outbound is enabled
   * - Fallback webhook is NOT enabled
   */
  const showOutboundSection =
    outbound.enabled === true &&
    (fallbackWebhook === 0 ||
      fallbackWebhook === "0" ||
      fallbackWebhook === false ||
      !fallbackWebhook);

  console.log(
    "Rendering OutboundVoiceWebhook with fallbackWebhook:",
    fallbackWebhook,
    "showOutboundSection:",
    showOutboundSection
  );

  /**
   * Fetch outbound-enabled workspaces on first render.
   * Data is cached in Redux, so we avoid refetching
   * if workspaces already exist.
   */
  useEffect(() => {
      dispatch(fetchOutboundWorkspaces());
  }, [dispatch]);

  /**
   * Helper to fetch parsed fields for a workspace
   *
   * OUTPUT:
   * - Array of parsed webhook fields for given workspace
   */
  const getParsedFields = (workspaceId) =>
    outbound.workspaces.find((w) => w.workspace_id === workspaceId)
      ?.parsed_fields || [];

  return (
    <>
      {/* =====================================================
          Enable Outbound Voice Toggle
         ===================================================== */}
      <div className="webhook-section-card">
        <div className="webhook-section-header">
          <div className="webhook-section-title">
            <label className="webhook-main-label">Outbound Voice</label>

          {/* Toggle outbound voice feature */}
          <Switch
              onChange={(v) => dispatch(toggleOutbound(v))}
            checked={outbound.enabled}
            onColor="#86d3ff"
            onHandleColor="#2693e6"
            handleDiameter={30}
            uncheckedIcon={false}
            checkedIcon={false}
            boxShadow="0px 1px 5px rgba(0, 0, 0, 0.6)"
            activeBoxShadow="0px 0px 1px 10px rgba(0, 0, 0, 0.2)"
            height={20}
            width={48}
            className="react-switch"
            id="material-switch"
          />
        </div>
      </div>

      {/* =====================================================
          Outbound Workspace Configuration Section
         ===================================================== */}
      {showOutboundSection && (
          <div className="webhook-card-content">
            <div className="webhook-filter-rule">
              <label className="webhook-filter-label">
              Add Outbound Workspaces
            </label>

            {/* Workspace multi-select dropdown */}
            <Dropdown
                placeholder="Searchable Select Workspaces"
              fluid
              multiple
              search
              selection
                icon="search"
                className="webhook-searchable-dropdown"
              options={
                  outbound.workspaces.length
                    ? outbound.workspaces.map((w) => ({
                      key: w.workspace_id,
                      text: w.workspace_name,
                      value: w.workspace_id,
                    }))
                  : []
              }
                value={outbound.selectedWorkspaces}
              onChange={(e, d) => dispatch(setWorkspaces(d.value))}
            />

            {/* =====================================================
                Per-Workspace Rule Configuration
               ===================================================== */}
            {outbound.selectedWorkspaces.map((wsId) => {
              const fields = getParsedFields(wsId);
              const workspaceName =
                outbound.workspaces.find(
                  (w) => w.workspace_id === wsId
                )?.workspace_name || "Workspace";

              return (
                <div
                  key={wsId}
                  className="webhook-workspace-config"
                >
                  {/* No fields available */}
                  {fields.length === 0 ? (
                    <label className="webhook-filter-label">
                      No parsed webhook fields are available for{" "}
                      <b>{workspaceName}</b>
                    </label>
                  ) : (
                  <>
                      <label className="webhook-filter-label">
                        Add webhook filtering rules for{" "}
                        <b>{workspaceName}</b>
                      </label>

                      {/* Select fields to apply filters on */}
                      <Dropdown
                        placeholder="Select filtering fields"
                        fluid
                        multiple
                        selection
                        options={fields.map((f) => ({
                          key: f.key,
                          text: f.key,
                          value: f.key,
                        }))}
                        value={outbound.parsedFields[wsId] || []}
                        onChange={(e, d) =>
                          dispatch(
                            setParsedFields({
                              workspaceId: wsId,
                              fields: d.value,
                            })
                          )
                        }
                      />

                      {/* =====================================================
                          Field Value Selection
                         ===================================================== */}
                      {(outbound.parsedFields[wsId] || []).map((field) => {
                        const f = fields.find((x) => x.key === field);
                        if (!f?.values?.length) return null;

                        return (
                          <div key={field} className="webhook-filter-rule">
                            <label className="webhook-filter-label">
                              Webhook will trigger if{" "}
                              <b>{field}</b> option equals
                            </label>

                            <Dropdown
                              fluid
                              multiple
                              selection
                              placeholder={`Choose ${field} value`}
                              options={f.values.map((v) => ({
                                key: v,
                                text: v,
                                value: v,
                              }))}
                              value={
                                outbound.selectedValues[wsId]?.[field] || []
                              }
                              onChange={(e, d) =>
                                dispatch(
                                  setSelectedValues({
                                    workspaceId: wsId,
                                    field,
                                    values: d.value,
                                  })
                                )
                              }
                            />
                          </div>
                        );
                      })}

                      {/* =====================================================
                          Required Fields Rule
                         ===================================================== */}
                      <div className="webhook-checkbox-container">
                        <Checkbox
                          checked={
                            outbound.requiredSettings[wsId] || false
                          }
                          onChange={(e, data) =>
                            dispatch(
                              setRequiredSetting({
                                workspaceId: wsId,
                                value: data.checked,
                              })
                            )
                          }
                        />
                        <label
                          className="webhook-checkbox-label"
                          onClick={() =>
                            dispatch(
                              setRequiredSetting({
                                workspaceId: wsId,
                                value: !outbound.requiredSettings[wsId],
                              })
                            )
                          }
                        >
                          Use the "if all required fields are gathered" rule for {workspaceName} to determine when to pass a payload
                        </label>
                      </div>
                    </>
                  )}
                </div>
              );
            })}
            </div>
        </div>
      )}
      </div>
    </>
  );
};

export default OutboundVoiceWebhook;
